import { ICameraCCTV } from '@api/models';
import { Button, Card, CircularProgress, IconButton, Label } from '@hyperflake/react-ui-library';
import { CopyIcon, CrossIcon, PlusIcon, UnlockIcon } from '@icon/index';
import { CardBody, CardHeader } from '@library/cards';
import { FormHelperText } from '@library/forms';
import { Popover } from '@library/popover';
import { useQueryClient } from '@tanstack/react-query';
import { ErrorMessage, FieldArray, Form, Formik } from 'formik';
import useDeleteCctvCameraChannel from 'modules/cctv-cameras/hooks/useDeleteCctvCameraChannel';
import useUpsertCctvCameraChannels from 'modules/cctv-cameras/hooks/useUpsertCctvCameraChannels';
import { FormikInput, FormikSwitch } from 'modules/shared/features/formik';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

type Props = {
    clientId: string;
    projectId: string;
    cctvCamera: ICameraCCTV;
};

const ChannelSchema = Yup.object({
    _id: Yup.string().optional(),
    number: Yup.mixed().required('Channel number is required'),
    hlsUrl: Yup.string().required('HSL url is required'),
    webrtcUrl: Yup.string().optional(),
    ptzControl: Yup.object({
        isAvailable: Yup.boolean().required(),
        deviceId: Yup.string().when('isAvailable', {
            is: true,
            then: (s) => s.required('Device ID is required when PTZ is available'),
            otherwise: (s) => s.optional(),
        }),
    }),
});

const Schema = Yup.object({
    default_channel: Yup.mixed().optional(),
    channels: Yup.array().of(ChannelSchema).min(1, 'At least one channel is required'),
});

const CctvChannelUpdateForm = ({ clientId, projectId, cctvCamera }: Props) => {
    const queryClient = useQueryClient();
    const [openPopoverId, setOpenPopoverId] = useState<number | null>(null);

    // Local camera state – source of truth for existing channels
    const [cameraState, setCameraState] = useState<ICameraCCTV>(cctvCamera);

    // per-channel lock/unlock state (for existing channels only)
    const [editableMap, setEditableMap] = useState<Record<string, boolean>>({});
    const [savingChannelId, setSavingChannelId] = useState<string | null>(null);

    const { remove: deleteChannel, isLoading: isDeleting } = useDeleteCctvCameraChannel(
        clientId,
        projectId,
        cameraState._id
    );

    const { createAsync, isCreating, updateAsync, isUpdating } = useUpsertCctvCameraChannels(
        clientId,
        projectId,
        cameraState._id,
        cameraState
    );

    // Keep cameraState in sync if parent passes a new camera (e.g. from query)
    useEffect(() => {
        setCameraState(cctvCamera);
    }, [cctvCamera._id, cctvCamera.channels?.length]);

    // Any channel whose _id is in this set is treated as "existing"
    const existingChannelIds = useMemo(
        () =>
            new Set(
                (cameraState.channels ?? [])
                    .map((ch: any) => ch?._id)
                    .filter(Boolean)
                    .map((id: any) => String(id))
            ),
        [cameraState.channels]
    );

    // When cameraState.channels changes (e.g. after create/update),
    // lock all existing rows by default
    useEffect(() => {
        setEditableMap({});
    }, [cameraState.channels?.length]);

    const initialValues = useMemo(
        () => ({
            default_channel: cameraState?.defaultChannelNumber ?? '',
            channels: cameraState?.channels?.map((ch: any) => ({
                _id: ch._id,
                number: ch.number ?? '',
                hlsUrl: ch.hlsUrl ?? '',
                webrtcUrl: ch.webrtcUrl ?? '',
                folder: ch.folder ?? '',
                ptzControl: {
                    isAvailable: !!ch?.ptzControl?.isAvailable,
                    deviceId: ch?.ptzControl?.deviceId ?? '',
                },
            })) ?? [
                {
                    number: '',
                    hlsUrl: '',
                    webrtcUrl: '',
                    folder: '',
                    ptzControl: { isAvailable: false, deviceId: '' },
                },
            ],
        }),
        [cameraState]
    );

    // SUBMIT → create only new channels (those not in existingChannelIds)
    const handleSubmit = async (values: typeof initialValues) => {
        const newChannels = values.channels.filter((ch: any) => {
            const id = (ch as any)?._id;
            // if id is not in existingChannelIds, treat as new
            return !id || !existingChannelIds.has(String(id));
        });

        if (!newChannels.length) {
            toast.info('No new channels to create.');
            return;
        }

        try {
            const updatedCamera = (await createAsync({ channels: newChannels })) as ICameraCCTV;

            // Immediately update local cameraState so newly created rows are "existing"
            if (updatedCamera) {
                setCameraState(updatedCamera);
            }

            // optional extra refetch
            await queryClient.invalidateQueries({
                queryKey: ['cctv-camera-query', cameraState._id],
            });

            toast.success('New channels created.');
            // After this:
            // - cameraState.channels includes the created channels (with _id)
            // - existingChannelIds includes them
            // - useEffect reset editableMap => they are locked + disabled.
        } catch (err: any) {
            toast.error(err?.response?.data?.message || 'Failed to create channels.');
        }
    };

    const toggleChannelEdit = (id: string) => {
        setEditableMap((prev) => ({
            ...prev,
            [id]: !prev[id],
        }));
    };

    const handleChannelCancel = (
        index: number,
        id: string | undefined,
        setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
    ) => {
        const initialChannel = initialValues.channels[index];
        if (initialChannel) {
            setFieldValue(`channels.${index}`, initialChannel, false);
        }
        if (id) {
            setEditableMap((prev) => ({ ...prev, [id]: false }));
        }
    };

    // ROW SAVE → update existing channels
    const handleChannelSave = async (id: string | undefined, values: typeof initialValues) => {
        if (!id) return; // only for existing channels

        try {
            setSavingChannelId(id);
            const updatedCamera = (await updateAsync({ channels: values.channels })) as ICameraCCTV;

            if (updatedCamera) {
                setCameraState(updatedCamera);
            }

            await queryClient.invalidateQueries({ queryKey: ['cctv-camera-query', cameraState._id] });
            setEditableMap((prev) => ({ ...prev, [id]: false })); // lock again
            toast.success('Channel updated.');
        } catch (err: any) {
            toast.error(err?.response?.data?.message || 'Failed to update channel.');
        } finally {
            setSavingChannelId(null);
        }
    };

    return (
        <Formik initialValues={initialValues} validationSchema={Schema} enableReinitialize onSubmit={handleSubmit}>
            {({ values, setFieldValue }) => (
                <Form>
                    <Card className="mt-6">
                        <CardHeader>
                            <h6 className="text-base md:text-lg">Channel Details</h6>
                        </CardHeader>
                        <CardBody className="p-3 sm:p-4 md:p-6">
                            <FieldArray
                                name="channels"
                                render={(arrayHelpers) => (
                                    <div className="space-y-4 md:space-y-6">
                                        {values.channels.map((channel, index) => {
                                            const key = (channel as any)?._id ?? index;

                                            const defaultNumRaw = values.default_channel;
                                            const defaultNum =
                                                typeof defaultNumRaw === 'string'
                                                    ? Number(defaultNumRaw)
                                                    : defaultNumRaw;
                                            const numRaw = channel.number;
                                            const num = typeof numRaw === 'string' ? Number(numRaw) : numRaw;
                                            const isDefault = defaultNum === num;

                                            const id = (channel as any)?._id as string | undefined;

                                            // "existing" iff this channel's id is in the latest cameraState.channels
                                            const isExisting = !!(id && existingChannelIds.has(String(id)));

                                            // New channels (no id / not in existing set) are always editable.
                                            // Existing channels are locked unless explicitly unlocked via editableMap.
                                            const isEditable = !isExisting || (id ? !!editableMap[id] : true);
                                            const isRowOpen = openPopoverId === index;
                                            const isSavingThisRow = id && savingChannelId === id;

                                            return (
                                                <div key={key} className="rounded-lg border p-3 sm:p-4 md:p-5">
                                                    {/* Row actions: Edit/Cancel/Save + Delete */}
                                                    <div className="flex items-center justify-between gap-2 sm:gap-4 pb-3">
                                                        <div className="flex items-center gap-2">
                                                            {isExisting &&
                                                                (!isEditable ? (
                                                                    <Button
                                                                        type="button"
                                                                        size="xs"
                                                                        variant="shaded"
                                                                        onClick={() => toggleChannelEdit(id!)}
                                                                    >
                                                                        <UnlockIcon
                                                                            width={18}
                                                                            height={22}
                                                                            className="text-grayscale-600 mr-1"
                                                                        />
                                                                        Edit
                                                                    </Button>
                                                                ) : (
                                                                    <>
                                                                        <Button
                                                                            type="button"
                                                                            size="xs"
                                                                            variant="shaded"
                                                                            disabled={isUpdating}
                                                                            onClick={() =>
                                                                                handleChannelCancel(
                                                                                    index,
                                                                                    id,
                                                                                    setFieldValue
                                                                                )
                                                                            }
                                                                        >
                                                                            Cancel
                                                                        </Button>
                                                                        <Button
                                                                            type="button"
                                                                            size="xs"
                                                                            disabled={isUpdating}
                                                                            onClick={() =>
                                                                                handleChannelSave(id, values)
                                                                            }
                                                                        >
                                                                            {isSavingThisRow ? (
                                                                                <CircularProgress size={14} />
                                                                            ) : (
                                                                                'Save'
                                                                            )}
                                                                        </Button>
                                                                    </>
                                                                ))}
                                                        </div>

                                                        <Popover
                                                            placement="left"
                                                            open={isRowOpen}
                                                            setOpen={(next) => {
                                                                const resolved =
                                                                    typeof next === 'function'
                                                                        ? (next as (prev: boolean) => boolean)(
                                                                              isRowOpen
                                                                          )
                                                                        : next;
                                                                setOpenPopoverId(resolved ? index : null);
                                                            }}
                                                            contentClassName="px-6 py-5 rounded-2xl shadow-lg w-[280px] sm:w-[320px]"
                                                            content={
                                                                <div>
                                                                    <p className="text-sm text-grayscale-500">
                                                                        Are you sure you want to remove this channel?
                                                                    </p>
                                                                    <div className="mt-4 flex items-center gap-3">
                                                                        <Button
                                                                            type="button"
                                                                            size="xs"
                                                                            color="primary"
                                                                            disabled={isDeleting}
                                                                            onClick={async () => {
                                                                                if (values.channels.length <= 1) {
                                                                                    toast.error(
                                                                                        'At least one channel is required.'
                                                                                    );
                                                                                    return;
                                                                                }

                                                                                const ch = values.channels[index];
                                                                                const channelId = (ch as any)?._id;

                                                                                try {
                                                                                    if (channelId) {
                                                                                        await deleteChannel(channelId);
                                                                                    }
                                                                                    arrayHelpers.remove(index);
                                                                                    setOpenPopoverId(null);
                                                                                    await queryClient.invalidateQueries(
                                                                                        {
                                                                                            queryKey: [
                                                                                                'cctv-camera-query',
                                                                                                cameraState._id,
                                                                                            ],
                                                                                        }
                                                                                    );
                                                                                } catch (err: any) {
                                                                                    toast.error(
                                                                                        err?.data?.message ||
                                                                                            'Failed to delete channel.'
                                                                                    );
                                                                                }
                                                                            }}
                                                                        >
                                                                            {isDeleting ? 'Deleting...' : 'Delete'}
                                                                        </Button>

                                                                        <Button
                                                                            type="button"
                                                                            size="xs"
                                                                            variant="shaded"
                                                                            onClick={() => setOpenPopoverId(null)}
                                                                        >
                                                                            Cancel
                                                                        </Button>
                                                                    </div>
                                                                </div>
                                                            }
                                                        >
                                                            {values.channels.length > 1 && (
                                                                <button
                                                                    type="button"
                                                                    className={`mt-2 ${
                                                                        values.channels.length <= 1
                                                                            ? 'cursor-not-allowed opacity-40'
                                                                            : 'cursor-pointer'
                                                                    }`}
                                                                    onClick={() => {
                                                                        if (values.channels.length <= 1) return;
                                                                        setOpenPopoverId(index);
                                                                    }}
                                                                >
                                                                    <CrossIcon className="text-danger" />
                                                                </button>
                                                            )}
                                                        </Popover>
                                                    </div>

                                                    {/* Fields */}
                                                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-12 gap-2 sm:gap-2">
                                                        {/* number */}
                                                        <div className="md:col-span-12">
                                                            <Label
                                                                htmlFor={`channels.${index}.number`}
                                                                className="text-sm"
                                                            >
                                                                Channel Number {isDefault && '(Default)'}
                                                            </Label>
                                                            <FormikInput
                                                                type="number"
                                                                name={`channels.${index}.number`}
                                                                placeholder="e.g. 0 or 1"
                                                                min={0}
                                                                disabled={!isEditable && isExisting}
                                                            />
                                                            <FormHelperText variant="error">
                                                                <ErrorMessage name={`channels.${index}.number`} />
                                                            </FormHelperText>
                                                        </div>

                                                        {/* HLS */}
                                                        <div className="md:col-span-12 relative">
                                                            <Label
                                                                htmlFor={`channels.${index}.hlsUrl`}
                                                                className="text-sm"
                                                            >
                                                                HLS URL
                                                            </Label>
                                                            <FormikInput
                                                                type="text"
                                                                name={`channels.${index}.hlsUrl`}
                                                                placeholder="HLS URL"
                                                                className="pr-10"
                                                                disabled={!isEditable && isExisting}
                                                            />
                                                            {values.channels?.[index]?.hlsUrl && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => {
                                                                        const hls =
                                                                            values.channels?.[index]?.hlsUrl || '';
                                                                        if (!hls) return;
                                                                        navigator.clipboard.writeText(hls);
                                                                        toast.success('Copied Successfully.');
                                                                    }}
                                                                    className="absolute right-2 top-[40px]"
                                                                    aria-label="Copy HLS URL"
                                                                >
                                                                    <CopyIcon />
                                                                </button>
                                                            )}
                                                            <FormHelperText variant="error">
                                                                <ErrorMessage name={`channels.${index}.hlsUrl`} />
                                                            </FormHelperText>
                                                        </div>

                                                        {/* WebRTC */}
                                                        <div className="md:col-span-12 relative">
                                                            <Label
                                                                htmlFor={`channels.${index}.webrtcUrl`}
                                                                className="text-sm"
                                                            >
                                                                WebRTC URL
                                                            </Label>
                                                            <FormikInput
                                                                type="text"
                                                                name={`channels.${index}.webrtcUrl`}
                                                                placeholder="WebRTC URL"
                                                                className="pr-10"
                                                                disabled={!isEditable && isExisting}
                                                            />
                                                            {!!values.channels?.[index]?.webrtcUrl && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => {
                                                                        const url =
                                                                            values.channels?.[index]?.webrtcUrl || '';
                                                                        if (!url) return;
                                                                        navigator.clipboard.writeText(url);
                                                                        toast.success('Copied Successfully.');
                                                                    }}
                                                                    className="absolute right-2 top-[40px]"
                                                                    aria-label="Copy WebRTC URL"
                                                                >
                                                                    <CopyIcon />
                                                                </button>
                                                            )}
                                                            <FormHelperText variant="error">
                                                                <ErrorMessage name={`channels.${index}.webrtcUrl`} />
                                                            </FormHelperText>
                                                        </div>

                                                        {/* PTZ */}
                                                        <div className="md:col-span-12">
                                                            <div className="flex gap-2">
                                                                <Label
                                                                    className="mb-0 text-sm"
                                                                    htmlFor={`channels.${index}.ptzControl.isAvailable`}
                                                                >
                                                                    PTZ Available
                                                                </Label>
                                                                <FormikSwitch
                                                                    name={`channels.${index}.ptzControl.isAvailable`}
                                                                    disabled={!isEditable && isExisting}
                                                                />
                                                            </div>
                                                            <FormHelperText variant="error">
                                                                <ErrorMessage
                                                                    name={`channels.${index}.ptzControl.isAvailable`}
                                                                />
                                                            </FormHelperText>

                                                            {values.channels[index].ptzControl?.isAvailable && (
                                                                <div className="mt-3 sm:mt-4">
                                                                    <Label
                                                                        htmlFor={`channels.${index}.ptzControl.deviceId`}
                                                                        className="text-sm"
                                                                    >
                                                                        PTZ Device ID
                                                                    </Label>
                                                                    <FormikInput
                                                                        type="text"
                                                                        name={`channels.${index}.ptzControl.deviceId`}
                                                                        placeholder="Enter device id"
                                                                        disabled={!isEditable && isExisting}
                                                                    />
                                                                    <FormHelperText variant="error">
                                                                        <ErrorMessage
                                                                            name={`channels.${index}.ptzControl.deviceId`}
                                                                        />
                                                                    </FormHelperText>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}

                                        <div className="pt-1">
                                            <IconButton
                                                className="mt-1 sm:mt-2"
                                                type="button"
                                                onClick={() =>
                                                    arrayHelpers.push({
                                                        number: '',
                                                        hlsUrl: '',
                                                        webrtcUrl: '',
                                                        folder: '',
                                                        ptzControl: { isAvailable: false, deviceId: '' },
                                                    })
                                                }
                                                size="sm"
                                                color="secondary"
                                            >
                                                <PlusIcon />
                                            </IconButton>
                                        </div>
                                    </div>
                                )}
                            />

                            <div className="mt-4 sm:mt-6">
                                <Button type="submit" className="w-full sm:w-auto" size="lg" disabled={isCreating}>
                                    {isCreating ? <CircularProgress className="text-white" size={24} /> : 'Submit'}
                                </Button>
                            </div>
                        </CardBody>
                    </Card>
                </Form>
            )}
        </Formik>
    );
};

export default CctvChannelUpdateForm;
